import React, { ReactNode } from "react";

import cn from "classnames";
import { defineMessages, IntlShape } from "react-intl";

import LeadFinderSubscriptionPlan, {
  planToUpgradePlansMap,
} from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { Button, showModal } from "@mapmycustomers/ui";

import upsellImage from "@app/assets/map/leadFinderUpsell.svg";
import { LeadFinderUpsellBulletPoints } from "@app/component/leadFinder/components";
import { LEAD_FINDER_UPSELL_URL } from "@app/util/consts";
import { planUpgradeUrls } from "@app/util/leadFinder/consts";
import { planMessages } from "@app/util/leadFinder/messages";

import styles from "./ShowLeaderFinderUpsell.module.scss";

const messages = defineMessages({
  cancel: {
    id: "map.tool.leadFinder.upsell.cancel.button.title",
    defaultMessage: "Cancel",
    description: "Cancel button",
  },
  contactOwner: {
    id: "map.tool.leadFinder.upsell.contactOwner.description",
    defaultMessage: "Let your manager or administrator know you’d like to add Lead Finder!",
    description: "Contact owner description",
  },
  interested: {
    id: "map.tool.leadFinder.upsell.interested",
    defaultMessage: "I’m Interested",
    description: "Interested button title",
  },
  interestedDescription: {
    id: "map.tool.leadFinder.upsell.interested.description",
    defaultMessage: "Click “I’m interested” and we’ll have your account manager reach out.",
    description: "I am interested message for owners",
  },
  okText: {
    id: "map.tool.leadFinder.upsell.ok.button.title",
    defaultMessage: "Okay",
    description: "Ok button",
  },
  purchaseModalTitle: {
    id: "map.tool.leadFinder.upsell.purchase.modalTitle",
    defaultMessage: "Add on <b>Lead Finder</b> to prospect from anywhere",
    description: "Upgrade lead finder title",
  },
  upgradeModalTitle: {
    id: "map.tool.leadFinder.upsell.upgrade.modalTitle",
    defaultMessage: "Upgrade for more <b>Lead Finder</b> daily searches",
    description: "Upgrade lead finder title",
  },
  upgradeTitle: {
    id: "map.tool.leadFinder.upsell.upgrade.title",
    defaultMessage: "Upgrade from your basic lead gen for:",
    description: "Upgrade from basic",
  },
});

const showLeadFinderUpsell = (
  leadGenPlan: LeadFinderSubscriptionPlan | undefined,
  intl: IntlShape,
  isOwner: boolean
) => {
  const noPlan = leadGenPlan === LeadFinderSubscriptionPlan.NONE;
  const handleOk = () => {
    window.open(LEAD_FINDER_UPSELL_URL, "_blank");
  };

  showModal({
    cancelButtonProps: { hidden: !isOwner },
    cancelText: intl.formatMessage(messages.cancel),
    centered: true,
    content: (
      <div className={cn(styles.container, { [styles.upgrade]: !noPlan })}>
        <div className={styles.header}>
          <img alt="lead finder upsell image" className={styles.image} src={upsellImage} />
          <div className={styles.title}>
            {intl.formatMessage(noPlan ? messages.purchaseModalTitle : messages.upgradeModalTitle, {
              b: (text: ReactNode) => <span className={styles.primary}>{text}</span>,
            })}
          </div>
        </div>

        {noPlan && <LeadFinderUpsellBulletPoints intl={intl} />}

        <div className={styles.options}>
          {planToUpgradePlansMap[leadGenPlan ?? LeadFinderSubscriptionPlan.NONE].map((plan) => (
            <div className={styles.option} key={plan}>
              <div>
                {intl.formatMessage(planMessages[plan], {
                  b: (text: ReactNode) => <b className={styles.emphasise}>{text}</b>,
                  isOwner,
                  noPlan,
                })}
              </div>
              {isOwner && (
                <Button href={planUpgradeUrls[plan]} target="_blank" type="primary">
                  {intl.formatMessage(messages.interested)}
                </Button>
              )}
            </div>
          ))}
        </div>

        <span className={styles.bottomText}>
          {intl.formatMessage(isOwner ? messages.interestedDescription : messages.contactOwner)}
        </span>
      </div>
    ),
    icon: null,
    okButtonProps: { hidden: isOwner },
    okText: intl.formatMessage(messages.okText),
    onOk: isOwner ? handleOk : undefined,
    type: "confirm",
    width: "512px",
    wrapClassName: styles.upsellWrapper,
  });
};

export default showLeadFinderUpsell;
